import type {
  AccountView,
  ConfidenceLog,
  ConvictionRoster,
  ConvictionScorecard,
  ConvictionStatus,
  DrawdownFloorStatus,
  EngineStatus,
  EnsembleStatus,
  EquityHistory,
  EquityRange,
  OverlayView,
  SectorContext,
  SectorExposure,
  SectorHistory,
  SectorShiftConfig,
  SectorShiftProposal,
  SectorShiftProposalStatus,
  SectorShiftRange,
  SectorShiftRuns,
  SectorShiftVerdict,
} from '../types'

const API_BASE = '/api'

// Appends `?account_id=<id>` (or `&account_id=<id>` if the endpoint already
// has query params) when accountId is provided; returns the endpoint
// unchanged otherwise. Account scoping for account-scoped endpoints is done
// this way — via ActiveAccountMiddleware reading the query param — NOT via
// a route-specific path segment. Omitting it targets the default
// (research/active-pointer) account.
function withAccount(endpoint: string, accountId?: number): string {
  if (accountId === undefined) return endpoint
  const sep = endpoint.includes('?') ? '&' : '?'
  return `${endpoint}${sep}account_id=${accountId}`
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`

  const config: RequestInit = {
    // credentials: 'include' is required so the HttpOnly session cookie
    // (`tradingplatform_session`) is sent on every API call. Without it,
    // every authenticated endpoint returns 401 — the cookie is set by
    // POST /api/v1/auth/login and read back here.
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  }

  const response = await fetch(url, config)

  if (response.status === 401) {
    // Cookie missing or expired. Tell the AuthContext (via window event,
    // since this module can't import React) to redirect to /login.
    // We intentionally don't read the response body — the API may or
    // may not include a JSON detail; we just need the status code.
    window.dispatchEvent(new CustomEvent('auth:unauthorized'))
    throw new ApiError(401, 'Not authenticated')
  }

  if (!response.ok) {
    const message = await response.text()
    throw new ApiError(response.status, message || response.statusText)
  }

  // 204 No Content (and any empty body) has no JSON to parse — calling
  // response.json() on it throws "Unexpected end of JSON input". Endpoints
  // like DELETE /v1/auth/agent-tokens return 204, so resolve to undefined
  // for those rather than blowing up. Callers type these as `request<void>`.
  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

export { request }

// Named shapes for the Ops desk faces (opsSummaries.ts). Naming only: each is the
// inline shape its endpoint already returned.
export interface HealthData {
  system_status: string
  market_status: string
  active_positions: number
  pending_signals: number
  last_updated: string
}

export interface AgentRow {
  name: string
  status: string
  lastHeartbeat: string
  tasksProcessed: number
  avgLatency: number
  currentTask: string | null
}

export interface ServicesData {
  services: Array<{
    name: string
    connected: boolean
    circuit_state: string
    failure_count: number
    rate_limit: { limit: number; remaining: number; period_seconds: number } | null
    detail: string | null
  }>
}

// System endpoints
export const systemApi = {
  getHealth: () => request<HealthData>('/system/health'),

  getAgents: () => request<AgentRow[]>('/system/agents'),

  getConnections: () => request<{
    polygon: { connected: boolean; lastPing: number; rateLimitRemaining: number }
    tiingo: { connected: boolean; lastPing: number }
    alpaca: { connected: boolean; accountStatus: string }
    database: { connected: boolean; poolSize: number }
    redis: { connected: boolean; hitRate: number }
  }>('/system/connections'),

  getCircuitBreakers: () => request<Array<{
    name: string
    status: string
    triggered_at: string | null
    cooldown_remaining: number
  }>>('/system/circuit-breakers'),

  getServices: () => request<ServicesData>('/system/services'),
}

export interface SignalPipelineData {
  received: number
  queued: number
  ordered: number
  executed: number
  rejected: number
  reject_reasons: Array<{ code: string; label: string; count: number }>
  cumulative_since: string
  session?: {
    session_date: string
    is_current: boolean
    received: number
    queued: number
    ordered: number
    executed: number
    rejected: number
    reject_reasons: Array<{ code: string; label: string; count: number }>
  }
}

// Signals endpoints
export const signalsApi = {
  getPending: () => request<Array<{
    id: string
    symbol: string
    action: 'BUY' | 'SELL'
    price: number
    quantity: number
    confidence: number
    reasoning: string[]
    expires_at: string
    source: string
    risk_score: number
    created_at: string
  }>>('/signals/pending'),

  execute: (signalId: string) =>
    request<{ success: boolean; order_id?: string; message?: string }>(
      `/signals/pending/${signalId}/execute`,
      { method: 'POST' }
    ),

  reject: (signalId: string, reason: string) =>
    request<{ success: boolean }>(
      `/signals/pending/${signalId}/reject`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }
    ),

  // Backend wraps the list in {signals, total, limit, offset} (see
  // GET /signals/history) — unwrap so callers get the array directly.
  getHistory: async (limit = 50) => {
    const res = await request<{
      signals: Array<{
        id: string
        symbol: string
        action: string
        price: number
        quantity: number
        confidence: number
        status: string
        executed_at?: string
        rejected_at?: string
        expired_at?: string
        rejected_reason?: string
      }>
      total: number
      limit: number
      offset: number
    }>(`/signals/history?limit=${limit}`)
    return res.signals
  },

  getStats: () => request<{
    total_signals: number
    executed: number
    rejected: number
    expired: number
    avg_confidence: number
  }>('/signals/stats'),

  getPipeline: () => request<SignalPipelineData>('/signals/pipeline'),
}

// Executions endpoints
export const executionsApi = {
  getRecent: (limit = 50) =>
    request<Array<{
      symbol: string
      side: 'buy' | 'sell'
      quantity: number
      price: number
      executed_at: string
      commission: number
    }>>(`/executions/recent?limit=${limit}`),
}

export interface RiskMetrics {
  daily_drawdown: number
  daily_drawdown_limit: number
  total_drawdown: number
  total_drawdown_limit: number
  portfolio_exposure: number
  max_exposure: number
  var_1d: number
  sharpe_ratio: number
  // Returned by the API but not read by the RiskMonitor panel (ruling 50).
  position_count?: number
  max_position_count?: number
  gross_leverage?: number
  max_gross_leverage?: number
  circuit_breaker_tripped?: boolean
}

// Risk endpoints
export const riskApi = {
  // Drawdown floor (wargame 22) — account-scoped via ?account_id=; query keys MUST
  // include accountId. clear is admin-session-only server-side (400 without confirm,
  // 409 with a `detail` string on refusal).
  drawdownFloor: (accountId: number) =>
    request<DrawdownFloorStatus>(withAccount('/v1/risk/drawdown-floor', accountId)),

  clearDrawdownFloor: (accountId: number, note: string) =>
    request<DrawdownFloorStatus>(
      withAccount('/v1/risk/drawdown-floor/clear', accountId),
      { method: 'POST', body: JSON.stringify({ confirm: true, note }) }),

  getMetrics: () => request<RiskMetrics>('/risk/metrics'),

  getLimits: () => request<{
    max_position_size: number
    max_portfolio_exposure: number
    max_daily_loss: number
    max_drawdown: number
    position_limits: Record<string, number>
  }>('/risk/limits'),

  getCircuitBreaker: () => request<{
    status: string
    triggered_at: string | null
    cooldown_remaining: number
    trigger_reason: string | null
  }>('/risk/circuit-breaker'),

  triggerCircuitBreaker: (reason: string) =>
    request<{ success: boolean }>(
      '/risk/circuit-breaker',
      {
        method: 'POST',
        body: JSON.stringify({ action: 'trigger', reason }),
      }
    ),

  resetCircuitBreaker: () =>
    request<{ success: boolean }>(
      '/risk/circuit-breaker',
      {
        method: 'POST',
        body: JSON.stringify({ action: 'reset' }),
      }
    ),

  getAlerts: () => request<Array<{
    id: string
    type: string
    severity: string
    title: string
    message: string
    created_at: string
    status: string
  }>>('/risk/alerts'),
}

// ML endpoints
export const mlApi = {
  getEnsemble: () => request<EnsembleStatus>('/ml/ensemble'),

  getModels: () => request<Array<{
    name: string
    status: string
    accuracy_7d: number
    predictions_today: number
    last_trained: string
  }>>('/ml/models'),

  getPredictions: (symbol: string) =>
    request<{
      symbol: string
      predictions: Array<{
        model: string
        direction: string
        confidence: number
        timestamp: string
      }>
    }>(`/ml/predictions/${symbol}`),

  getFeatureImportance: (modelName: string) =>
    request<{
      model: string
      features: Array<{ name: string; importance: number }>
    }>(`/ml/feature-importance/${modelName}`),
}

export interface Goal {
  id: string
  name: string
  type: string
  target: number
  current: number
  unit: string
  active: boolean
  progress?: number
  priority?: number
  createdAt?: string
  updatedAt?: string
}

// Goals endpoints
export const goalsApi = {
  getAll: () => request<Goal[]>('/goals'),

  create: (goal: {
    name: string
    type: string
    target: number
    unit: string
  }) =>
    request<{ id: string }>('/goals', {
      method: 'POST',
      body: JSON.stringify(goal),
    }),

  update: (goalId: string, updates: Partial<{
    name: string
    target: number
    active: boolean
  }>) =>
    request<{ success: boolean }>(`/goals/${goalId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),

  delete: (goalId: string) =>
    request<{ success: boolean }>(`/goals/${goalId}`, {
      method: 'DELETE',
    }),

  getProgress: () => request<{
    goals: Array<{
      id: string
      name: string
      progress_percent: number
      on_track: boolean
    }>
  }>('/goals/progress'),
}

// Positions endpoints
export const positionsApi = {
  getAll: (accountId?: number) => request<Array<{
    symbol: string
    quantity: number
    average_entry_price: number
    current_price: number
    market_value: number
    unrealized_pnl: number
    unrealized_pnl_pct: number
    unrealized_intraday_pnl: number | null
    unrealized_intraday_pnl_percent: number | null
    lastday_price: number | null
  }>>(withAccount('/positions', accountId)),

  get: (symbol: string, accountId?: number) => request<{
    symbol: string
    quantity: number
    average_entry_price: number
    current_price: number
    market_value: number
    unrealized_pnl: number
    unrealized_pnl_pct: number
    unrealized_intraday_pnl: number | null
    unrealized_intraday_pnl_percent: number | null
    lastday_price: number | null
  }>(withAccount(`/positions/${symbol}`, accountId)),

  close: (symbol: string) =>
    request<{ success: boolean; order_id?: string }>(
      `/positions/${symbol}/close`,
      { method: 'POST' }
    ),
}

// Account endpoint (trading router mounted at /api)
export const accountApi = {
  // Account-scoped via ?account_id= (ActiveAccountMiddleware). Omitting the id
  // targets the default/active-pointer account, unchanged. A registered but
  // dormant account (no live Book in the engine) is a 404 — a normal state the
  // caller renders as "no equity", not an error.
  // NOTE: `total_pnl` is 0 for any non-default account — the engine's
  // performance tracker is a singleton fed only by books[0] (AA3a gate), so
  // there is no per-book lifetime P&L to report yet.
  get: (accountId?: number) => request<{
    account_value: number | string
    cash: number | string
    buying_power: number | string
    total_positions: number
    total_pnl: number | string
    daily_pnl: number | string
  }>(withAccount('/account', accountId)),

  // Per-book daily equity history for the sparkline/trend chart (WARGAME 13).
  // 404s for a registered-but-dormant book — sparkline/overlay treat it as empty,
  // while the full chart shows an error empty-state.
  history: (accountId?: number, range: EquityRange = '1M') =>
    request<EquityHistory>(withAccount(`/account/history?range=${range}`, accountId)),
}

export interface BenchmarkWindowReturn {
  return_pct: number | null
  start: string | null
  end: string | null
}
export interface BenchmarkReturns {
  symbol: string
  as_of: string
  windows: Record<'day' | '1w' | '1m' | '3m', BenchmarkWindowReturn>
}

export const benchmarkApi = {
  // One payload carries every window — fetched once, reused across
  // timeframe switches. Not account-scoped (SPY is global).
  returns: () => request<BenchmarkReturns>('/benchmark/returns'),
}

// Engine endpoints
// NOTE: engine routes are mounted under /api/v1/engine, not /api, so we
// prefix explicitly to avoid polluting the rest of the client with /v1/.
export const engineApi = {
  // Triggers one workflow cycle via POST /api/v1/engine/workflow/execute.
  // Returns HTTP 200 even when the workflow ran but failed (success=false).
  // A non-2xx response means a genuine server/extreme failure.
  // Autonomy principle: success=false is a normal observable result; 5xx warrants
  // operator attention.
  executeWorkflow: (symbols?: string[]) =>
    request<{
      run_id: string
      success: boolean
      duration_ms: number
      error: string | null
      stages: {
        data_collected: number
        agents_executed: number
        ml_predictions: number
        signals_generated: number
        orders_executed: number
      }
    }>(
      '/v1/engine/workflow/execute',
      {
        method: 'POST',
        body: JSON.stringify(symbols && symbols.length > 0 ? { symbols } : {}),
      }
    ),

  // Return type is EngineStatus (types/index.ts), which now includes
  // `books: BookStatus[]` — the per-account book roster used by useBooks().
  // A disabled account (e.g. dormant live) will not appear in books[].
  getStatus: () => request<EngineStatus>('/v1/engine/status'),

  stop: () => request<{ status: string }>('/v1/engine/stop', { method: 'POST' }),
}

// Notifications endpoints (Web Push)
export const notificationsApi = {
  subscribe: (body: {
    endpoint: string
    keys: { p256dh: string; auth: string }
    user_agent: string
  }) =>
    request<{ success: boolean }>(
      '/notifications/subscribe',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    ),

  unsubscribe: (endpoint: string) =>
    request<{ success: boolean }>(
      '/notifications/unsubscribe',
      {
        method: 'POST',
        body: JSON.stringify({ endpoint }),
      }
    ),
}

export interface WorkflowRunRow {
  run_id: string
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  mode: string
  success: boolean
  error_message: string | null
  signals_generated: number
  signals_passed_sanity: number
  orders_placed: number
  fills_received: number
  skipped_reason: string | null
}

export interface AgentDecisionRow {
  agent_name: string
  symbol: string
  signal: string
  confidence: number
  timestamp: string | null
}

export interface SanityGateEventRow {
  rule_name: string
  decision: string
  symbol: string
  reason_detail: string
  timestamp: string | null
}

export interface RunDetailCounts {
  orders_placed: number
  fills_received: number
  signals_generated: number
  signals_passed_sanity: number
}

export interface AutoStopLast {
  concentration?: number
  daily_loss?: number
  broker_disconnect?: number
  sanity_veto_rate?: number
}

interface Bucket { runs: number; orders: number; vetoes: number }

// ~15 months — matches the D4 spec §6.1 Widget 9 "CloudWatch 15-month history"
const CLOUDWATCH_HISTORY_DAYS = 450

// Named response shapes of the audit endpoints (Ops faces annotate with these).
export interface AgentHealth24hData {
  window_hours: number
  agents: Array<{ agent_name: string; decisions: number; avg_confidence: number; last_decision_at: string | null }>
}

export interface VetoRates {
  days: number
  total_events: number
  rules: Array<{ rule_name: string; rejects: number; reject_rate: number }>
}

export interface CloudWatchData {
  available: boolean
  days?: number
  reason?: string
  metrics: Record<string, { timestamps: string[]; values: number[] }>
}

export const auditApi = {
  listRuns: (limit = 50, page = 1) =>
    request<{ total: number; page: number; limit: number; runs: WorkflowRunRow[] }>(
      `/v1/audit/workflow-runs?limit=${limit}&page=${page}`),
  runDetail: (runId: string) =>
    request<{
      run: WorkflowRunRow
      counts: RunDetailCounts
      agent_decisions: AgentDecisionRow[]
      sanity_gate_events: SanityGateEventRow[]
      note: string
    }>(`/v1/audit/workflow-runs/${encodeURIComponent(runId)}`),
  agentHealth: () => request<AgentHealth24hData>('/v1/audit/agent-health'),
  dailySummary: () =>
    request<{ today: Bucket; yesterday: Bucket; trailing_7d: Bucket }>(
      '/v1/audit/daily-summary'),
  vetoRates: (days = 7) => request<VetoRates>(`/v1/audit/veto-rates?days=${days}`),
  autoStopStatus: () =>
    request<{ available: boolean; tripped?: boolean; trip_reason?: Record<string, unknown> | null; last?: AutoStopLast; reason?: string }>(
      '/v1/audit/auto-stop-status'),
  cloudwatch: (days = CLOUDWATCH_HISTORY_DAYS) =>
    request<CloudWatchData>(`/v1/audit/cloudwatch-metrics?days=${days}`),
}

// ----- LLM Reasoning Loop -----

export interface LLMUsage {
  cost_today_usd: number
  calls_today: number
  calls_last_hour: number
  daily_budget_usd: number
  monthly_budget_usd: number
  max_calls_per_hour: number
  as_of: string
}

export interface LLMEnvelope {
  envelope_spec: Record<string, Record<string, number>>
  precedent_buckets: Record<string, Record<string, number>>
  precedent_decay_days: number
  precedent_negative_window_days: number
  cadence_enabled: boolean
  cadence_interval_seconds: number | null
}

export interface LLMHistoryItem {
  id: number
  timestamp: string | null
  backtest_run_id: string | null
  output_data: {
    decision?: string
    envelope_status?: string
    via_precedent?: number | null
    goal_type?: string
    current_value?: number
    proposed_value?: number
    violation_kind?: string
  }
}

export interface LLMPrecedentItem {
  id: number
  goal_type: string
  direction: 'tighten' | 'loosen' | string
  magnitude_bucket: 'small' | 'medium' | 'large' | string
  verdict: string
  applied: boolean
  outcome: 'positive' | 'negative' | null
  created_at: string | null
  outcome_resolved_at: string | null
  invalidated_at: string | null
  invalidate_reason: string | null
}

export interface BacktestLearning { key: string; severity: 'ok' | 'warn' | 'bad'; text: string }
export interface BacktestGoalDelta {
  goal_type: string; baseline_value: number; adjusted_value: number;
  direction: 'up' | 'down' | 'flat'; pct_change: number; applied_count: number;
  projected_pnl: number | null;
}
export interface BacktestGateCriterion {
  name: 'sharpe_lb' | 'spa' | 'degradation' | 'drawdown'
  passed: boolean
  value: number
}
export interface BacktestGateVerdict {
  strategy: string
  passed: boolean
  criteria: BacktestGateCriterion[]
}
export interface BacktestPerFold {
  strategy: string
  sharpes: number[]
}
export interface BacktestQueueReason {
  reason: string
  count: number
}
export interface BacktestSuggestedChange {
  param: string
  value: string
}
export interface BacktestRecommendation {
  key: string
  severity: 'critical' | 'warn' | 'info' | 'ok'
  title: string
  detail: string
  suggested_change: BacktestSuggestedChange | null
}
export interface BacktestSummary {
  run: string | null;
  progress: { run_id: string; cycles: number; spend_usd: number; avg_cost_usd: number;
    avg_latency_ms: number; status: 'running' | 'completed';
    started_at: string | null; last_cycle_at: string | null } | null;
  impact: { apply_rate: number; proposals_total: number; applied: number; queued: number;
    rejected: number; envelope_in: number; envelope_out: number; envelope_bypasses: number;
    hallucination_rate: number; avg_theses_per_cycle: number;
    avg_theses_proposal: number; avg_theses_no_proposal: number } | null;
  learnings: BacktestLearning[];
  goal_delta: BacktestGoalDelta[];
  gate?: BacktestGateVerdict[]
  per_fold?: BacktestPerFold[]
  queue_reasons?: BacktestQueueReason[]
  recommendations?: BacktestRecommendation[]
}

export interface DailyPerformanceDay {
  trade_date: string;
  summary: Record<string, number | string | null>;
  triggered: boolean;
  trigger_reasons: { leg: string; detail: string }[];
  gap_kind: string | null;
  corrective_backtest_run_id: string | null;
}

export interface ParameterOverrideView {
  id: number;
  param_key: string;
  value: number;
  bounds: { min: number; max: number; max_step: number } | null;
  status: string;
  effective_date: string;
  expires_date: string;
  source_backtest_run_id: string | null;
  reason: string | null;
  approved_by: string | null;
  applied_at: string | null;
  created_at?: string | null;
  age_trading_days?: number | null;
  is_stale?: boolean;
}

export const llmReasoningApi = {
  usage: () => request<LLMUsage>('/v1/llm-reasoning/usage'),
  envelope: () => request<LLMEnvelope>('/v1/llm-reasoning/envelope'),
  history: (limit = 50) =>
    request<{ limit: number; items: LLMHistoryItem[] }>(
      `/v1/llm-reasoning/history?limit=${limit}`,
    ),
  precedents: (activeOnly = true, limit = 50) =>
    request<{ active_only: boolean; limit: number; items: LLMPrecedentItem[] }>(
      `/v1/llm-reasoning/precedents?active_only=${activeOnly}&limit=${limit}`,
    ),
  setCadence: (enabled: boolean) =>
    request<{ enabled: boolean }>('/v1/llm-reasoning/cadence', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),
  invalidatePrecedent: (id: number, reason: string) =>
    request<{ precedent_id: number; invalidated: boolean }>(
      `/v1/llm-reasoning/precedents/${id}/invalidate`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
  backtestSummary: () =>
    request<BacktestSummary>('/v1/llm-reasoning/backtest/latest'),
}

// Adaptive Tuning types
export interface AdaptiveRunStatus {
  id: number
  status: string
  dispatch_outcome: string | null
  run_date: string
  abort_reason: string | null
  started_at: string | null
  finished_at: string | null
  corrective_status: string | null
}

export interface AdaptiveStatus {
  /**
   * The API container's CONFIGURED mode — not necessarily the loop's. The
   * adaptive loop runs in a separate ECS task that owns ADAPTIVE_TUNING_MODE,
   * so this silently reads "off" here whatever the loop is doing. Never render
   * it without checking `mode_is_authoritative` first; use `adaptiveModeWord`.
   */
  mode: string
  /** False when `mode` is a silent default rather than real configuration. */
  mode_is_authoritative?: boolean
  /** Proven from recorded runs: 'off' | 'not_off', or null with no runs yet. */
  mode_observed?: 'off' | 'not_off' | null
  paused: boolean
  last_run: { finished_at: string | null } | null
  next_run_at: string | null
  dispatches_today: number
  max_per_day: number
}

export interface AdaptiveRunsResponse {
  runs: AdaptiveRunStatus[]
}

// Adaptive Tuning endpoints
export const adaptiveApi = {
  dailyPerformance: (limit = 30) =>
    request<{ days: DailyPerformanceDay[] }>(
      `/v1/adaptive/performance/daily?limit=${limit}`,
    ),
  parameterOverrides: () =>
    request<{ overrides: ParameterOverrideView[] }>(
      `/v1/adaptive/parameters/overrides`,
    ),
  approveOverride: (id: number) =>
    request<ParameterOverrideView>(
      `/v1/adaptive/parameters/overrides/${id}/approve`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  rejectOverride: (id: number) =>
    request<ParameterOverrideView>(
      `/v1/adaptive/parameters/overrides/${id}/reject`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  status: () => request<AdaptiveStatus>(`/v1/adaptive/status`),
  runs: (limit = 14) => request<AdaptiveRunsResponse>(`/v1/adaptive/runs?limit=${limit}`),
}

// Operator controls — external-API spending kill switch
export interface ControlsBacktestRun {
  run_id: string
  status: string
  started_at: string
  completed_at: string | null
  duration_seconds: number | null
  mode: string | null
}

export interface ControlsStatus {
  external_apis_paused: boolean
  updated_at: string | null
  updated_by: string | null
  recent_backtest_runs: ControlsBacktestRun[]
}

export const controlsApi = {
  getStatus: () => request<ControlsStatus>('/controls/status'),
  pauseExternal: () =>
    request<ControlsStatus>('/controls/pause-external', { method: 'POST' }),
  resumeExternal: () =>
    request<ControlsStatus>('/controls/resume-external', { method: 'POST' }),
}

// Tax endpoints (Stage C1 backend)

// GET /api/tax/lots/closed response shapes (src/api/routes/tax.py,
// get_closed_tax_lots / _lot_dict / _summary_dict). Deliberately NOT at
// /tax/lots — that path is the open-lots/cursor route (get_tax_lots) and is
// a distinct, actively-consumed shape.
//
// Money fields are bare `str(float)` — e.g. "90.0", "-1830.5" — NOT
// 2-decimal-padded. Parse with Number(); format for display with usd().
export interface TaxLotRow {
  symbol: string
  shares: string
  acquired: string
  disposed: string
  proceeds: string
  cost_basis: string
  gain_loss: string
  reportable_gain_loss: string
  is_long_term: boolean
  is_wash_sale: boolean
  wash_disallowed: string
  wash_recovered: string
  holding_days: number
}

// Reshape of compute_year_aggregate's YearAggregate — the SAME function that
// materializes /tax/summary, so this reconciles with the card's headline
// figures by construction (see lib/taxReconcile.ts).
export interface TaxLotsSummary {
  reportable_net: string
  economic_net: string
  wash_disallowed_total: string
  wash_recovered_total: string
  short_term_net: string
  long_term_net: string
}

export interface TaxLotsClosedResponse {
  available: boolean
  tax_year: number
  // true when the year's dispositions exceed the 2000-row cap (_LOTS_CAP);
  // `lots` holds only the latest 2000 in that case, but `summary` still
  // covers the FULL year — so the reconciliation tripwire (summary vs card)
  // stays valid even when truncated. Only a Σ(displayed rows)-vs-summary
  // comparison would need to be skipped while truncated; we don't do that
  // comparison at all, so no special-casing is needed there.
  truncated: boolean
  lots: TaxLotRow[]
  summary: TaxLotsSummary | null
  // Additive; present (true) only on the route's exception path — absent on
  // every non-exception response, including the empty-year "no lots yet"
  // shape. Lets the card distinguish an actual read failure from a genuinely
  // empty ledger.
  degraded?: boolean
}

export const taxApi = {
  // account-scoped: pass accountId to scope to a specific book (query key
  // must include it, e.g. ['tax-summary', year, accountId]); omitted =
  // default (research/active-pointer) account.
  getSummary: (year: number, accountId?: number) => request<{
    available: boolean
    year: number
    short_term_net?: number
    long_term_net?: number
    total_net_gain_loss?: number
    wash_sale_disallowed?: number
    wash_sale_recovered?: number | null
    economic_net_gain_loss?: number | null
    transactions?: { short_term: number; long_term: number; wash_sales: number }
    income?: {
      dividend_qualified: number | null
      dividend_ordinary: number | null
      interest: number | null
      fees: number | null
    }
    loss_carryforward?: { used: number; remaining: number }
    estimated_total_tax?: number | null
    calculated_at?: string | null
  }>(withAccount(`/tax/summary?year=${year}`, accountId)),

  // Closed-lot ledger behind the tax summary (Task 5). Same withAccount +
  // year-param idiom as getSummary above.
  getLots: (year: number, accountId?: number) =>
    request<TaxLotsClosedResponse>(withAccount(`/tax/lots/closed?year=${year}`, accountId)),
}

// Account registry endpoints
export const accountsApi = {
  list: () => request<AccountView[]>('/v1/accounts'),
  overlay: (id: number) => request<OverlayView>(`/v1/accounts/${id}/overlay`),
}

// Conviction engine endpoints
// All three are account-scoped via the ?account_id= selector (see
// withAccount above). Query keys for these MUST include accountId, e.g.
// ['conviction-status', accountId], ['scorecard', accountId],
// ['confidence-log', accountId, days] — otherwise react-query will cache
// data from one book and serve it for another.
export const convictionApi = {
  status: (accountId?: number) =>
    request<ConvictionStatus>(withAccount('/v1/conviction/status', accountId)),
  scorecard: (accountId?: number) =>
    request<ConvictionScorecard>(withAccount('/v1/conviction/scorecard', accountId)),
  confidenceLog: (accountId?: number, days = 30) =>
    request<ConfidenceLog>(withAccount(`/v1/conviction/confidence-log?days=${days}`, accountId)),
  roster: (accountId?: number) =>
    request<ConvictionRoster>(withAccount('/v1/conviction/roster', accountId)),
}

// Sector Shift (wargame 21 I6) — account-scoped via ?account_id=. Query keys MUST
// include accountId (see SectorPanel). approve/reject/run are admin-session-only
// server-side (agent tokens are refused); the dashboard session cookie satisfies it.
export const sectorShiftApi = {
  exposure: (accountId?: number) =>
    request<SectorExposure>(withAccount('/v1/sector-shift/exposure', accountId)),
  context: (accountId?: number) =>
    request<SectorContext>(withAccount('/v1/sector-shift/context', accountId)),
  history: (accountId?: number, range: SectorShiftRange = '3M') =>
    request<SectorHistory>(withAccount(`/v1/sector-shift/history?range=${range}`, accountId)),
  verdicts: (accountId?: number, limit = 50) =>
    request<SectorShiftVerdict[]>(withAccount(`/v1/sector-shift/verdicts?limit=${limit}`, accountId)),
  proposals: (accountId?: number, status?: SectorShiftProposalStatus) =>
    request<SectorShiftProposal[]>(
      withAccount(`/v1/sector-shift/proposals${status ? `?status=${status}` : ''}`, accountId)),
  config: (accountId?: number) =>
    request<SectorShiftConfig>(withAccount('/v1/sector-shift/config', accountId)),
  runs: (accountId?: number, days = 7) =>
    request<SectorShiftRuns>(withAccount(`/v1/sector-shift/runs?days=${days}`, accountId)),
  approve: (id: number, accountId?: number) =>
    request<SectorShiftProposal>(
      withAccount(`/v1/sector-shift/proposals/${id}/approve`, accountId),
      { method: 'POST', body: JSON.stringify({}) }),
  reject: (id: number, accountId?: number) =>
    request<SectorShiftProposal>(
      withAccount(`/v1/sector-shift/proposals/${id}/reject`, accountId),
      { method: 'POST', body: JSON.stringify({}) }),
}

export { ApiError }
